/**
 * ScreenshotCropStage
 *
 * The interactive image panel shared by the Add and Edit screenshot flows: a
 * dropzone that accepts an image via drag, click, or paste, then frames it with
 * react-easy-crop and exports a standardized 2K WebP via {@link getCroppedWebp}.
 *
 * It owns all file/crop state internally and exposes the result imperatively
 * (`getCroppedFile`), so a parent form can pull the cropped file at submit time
 * without threading crop internals through props. In edit mode an
 * `existingImageUrl` backdrop shows the current screenshot until a replacement
 * is staged, so both "upload new" and "replace existing" reuse one component.
 */

import {
  useState, useCallback, useRef, useEffect, useImperativeHandle, forwardRef,
} from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import { getCroppedWebp, OUTPUT_WIDTH, OUTPUT_HEIGHT } from './cropImage';

export interface ScreenshotCropStageHandle {
  /**
   * The framed 2K WebP for the staged image, or null when nothing is staged.
   * Throws if an image is staged but its crop region is not ready yet.
   */
  getCroppedFile: () => Promise<File | null>;
  /** Whether an image is currently staged (drag/click/paste selected one). */
  hasStagedFile: () => boolean;
}

interface ScreenshotCropStageProps {
  /** Current screenshot to show as a backdrop until a replacement is staged. */
  existingImageUrl?: string;
  /** Stage this file immediately on mount (e.g. an image dropped onto the page). */
  prefillFile?: File | null;
  /** Register a document-level paste-to-stage handler while mounted. */
  enablePaste?: boolean;
  /** Notified whenever the staged file changes (null when cleared). */
  onStagedFileChange?: (file: File | null) => void;
  /** Outer dropzone layout hooks. */
  className?: string;
  style?: React.CSSProperties;
}

export const ScreenshotCropStage = forwardRef<ScreenshotCropStageHandle, ScreenshotCropStageProps>(
  function ScreenshotCropStage(
    { existingImageUrl, prefillFile, enablePaste, onStagedFileChange, className, style },
    ref,
  ) {
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const onStagedFileChangeRef = useRef(onStagedFileChange);
    useEffect(() => { onStagedFileChangeRef.current = onStagedFileChange; }, [onStagedFileChange]);

    const applyFile = useCallback((f: File | null) => {
      setFile(f);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return f ? URL.createObjectURL(f) : null;
      });
      // Reset the crop framing for the new image.
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      onStagedFileChangeRef.current?.(f);
    }, []);

    // Keep a stable ref so the paste effect never needs to re-register.
    const applyFileRef = useRef(applyFile);
    useEffect(() => { applyFileRef.current = applyFile; }, [applyFile]);

    // Stage a prefilled file once when provided.
    useEffect(() => {
      if (prefillFile) applyFileRef.current(prefillFile);
    }, [prefillFile]);

    // Paste-to-stage (only while enabled), ignoring pastes into form fields.
    useEffect(() => {
      if (!enablePaste) return;
      const handlePaste = (e: ClipboardEvent) => {
        const target = e.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable
        ) return;

        const imageItem = Array.from(e.clipboardData?.items ?? []).find(
          (item) => item.type.startsWith('image/'),
        );
        if (!imageItem) return;

        e.preventDefault();
        const f = imageItem.getAsFile();
        if (f) applyFileRef.current(f);
      };

      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }, [enablePaste]);

    // Revoke the object URL on unmount.
    useEffect(() => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
      hasStagedFile: () => file !== null,
      getCroppedFile: async () => {
        if (!file || !previewUrl) return null;
        if (!croppedAreaPixels) throw new Error('Adjust the crop before saving.');
        return getCroppedWebp(previewUrl, croppedAreaPixels);
      },
    }), [file, previewUrl, croppedAreaPixels]);

    const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
      setCroppedAreaPixels(areaPixels);
    }, []);

    const resetCrop = useCallback(() => {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped && dropped.type.startsWith('image/')) applyFile(dropped);
    }, [applyFile]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => setIsDragOver(false), []);

    const handleDropzoneClick = useCallback(() => fileInputRef.current?.click(), []);

    const handleFileInput = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        applyFile(e.target.files?.[0] ?? null);
      },
      [applyFile],
    );

    return (
      <div
        className={`dropzone${isDragOver ? ' is-dragover' : ''}${(previewUrl || existingImageUrl) ? ' dropzone--filled' : ''}${className ? ` ${className}` : ''}`}
        style={style}
        onClick={!previewUrl ? handleDropzoneClick : undefined}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleFileInput}
        />
        {previewUrl ? (
          <>
            <Cropper
              image={previewUrl}
              crop={crop}
              zoom={zoom}
              aspect={OUTPUT_WIDTH / OUTPUT_HEIGHT}
              minZoom={1}
              maxZoom={5}
              restrictPosition
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              style={{
                containerStyle: { position: 'absolute', inset: 0, background: 'transparent' },
                cropAreaStyle: { border: '2px dashed color-mix(in srgb, var(--color-white) 90%, transparent)', boxShadow: '0 0 0 9999px color-mix(in srgb, var(--color-black) 55%, transparent)' },
              }}
            />
            {/* Zoom + framing controls */}
            <div
              className="d-flex align-items-center gap-xs"
              style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
                padding: '10px 14px', background: 'color-mix(in srgb, var(--color-black) 55%, transparent)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-slate fs-xs" style={{ userSelect: 'none' }}>Zoom</span>
              <input
                className="range"
                type="range"
                min={1}
                max={5}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ flex: 1 }}
              />
              <button type="button" className="btn btn--outline btn--slate btn--sm" onClick={resetCrop}>
                Reset
              </button>
              <button
                type="button"
                className="btn btn--outline btn--slate btn--sm"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              >
                Replace
              </button>
            </div>
          </>
        ) : existingImageUrl ? (
          <>
            <img src={existingImageUrl} alt="Current screenshot" className="dropzone__preview" />
            {/* Replace hint overlaid on the current image. */}
            <div
              className="d-flex flex-column align-items-center gap-xs"
              style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
                padding: '10px 14px', background: 'color-mix(in srgb, var(--color-black) 55%, transparent)',
              }}
            >
              <p className="text-slate fs-sm m-0">Drop, click, or paste (Ctrl+V) to replace this image</p>
            </div>
          </>
        ) : (
          <div className="d-flex flex-column align-items-center gap-xs">
            <span className="fs-4xl">🖼️</span>
            <p className="text-slate fs-sm m-0">Drop an image here, click to upload, or paste (Ctrl+V)</p>
          </div>
        )}
      </div>
    );
  },
);
